// Add-to-calendar helpers — generates a standards-compliant .ics file (Apple
// Calendar, Outlook, etc.) and a Google Calendar deep-link, entirely client
// side. No backend or native bridge involved.

import { CALENDAR_ALERT_MINUTES_BEFORE } from '@/constants/timings';

export interface CalendarEventInput {
  title: string;
  description?: string;
  location?: string;
  /** ISO datetime string */
  startTime: string;
  /** ISO datetime string */
  endTime: string;
  uid?: string;
  /** Minutes before startTime to fire a reminder alarm. Defaults to CALENDAR_ALERT_MINUTES_BEFORE. */
  alertMinutesBefore?: number;
}

// Friendly notes text for the calendar event — avoids surfacing raw
// booking ids/uids to the member. Mentions the coach when there is one and
// reminds them of the check-in window.
export function buildBookingCalendarDescription(coachLabel?: string | null): string {
  const coachPart = coachLabel ? `With ${coachLabel}. ` : '';
  /* NOT "x minutes before". The API refuses check-in before the
     start time outright — `booking-admin.service.ts` throws on
     `now < booking.startTime` with no grace and no role override —
     so any lead time quoted here is a promise the server breaks. */
  return `${coachPart}Check-in opens when your session starts. See you at Tee Box!`;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

// RFC 5545 wants UTC "floating" form (YYYYMMDDTHHMMSSZ) — simplest and most
// compatible option since we already have absolute ISO instants and don't
// need to ship a VTIMEZONE block.
function toIcsUtc(dateInput: string): string {
  const d = new Date(dateInput);
  return (
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
  );
}

function escapeIcsText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

export function buildIcsContent(event: CalendarEventInput): string {
  const uid = event.uid || `${Date.now()}-${Math.random().toString(36).slice(2)}@teebox`;
  const alertMinutes = event.alertMinutesBefore ?? CALENDAR_ALERT_MINUTES_BEFORE;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Tee Box//Booking//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${toIcsUtc(new Date().toISOString())}`,
    `DTSTART:${toIcsUtc(event.startTime)}`,
    `DTEND:${toIcsUtc(event.endTime)}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
    ...(event.description ? [`DESCRIPTION:${escapeIcsText(event.description)}`] : []),
    ...(event.location ? [`LOCATION:${escapeIcsText(event.location)}`] : []),
    ...(alertMinutes > 0
      ? [
          'BEGIN:VALARM',
          'ACTION:DISPLAY',
          `DESCRIPTION:${escapeIcsText(event.title)}`,
          `TRIGGER:-PT${alertMinutes}M`,
          'END:VALARM',
        ]
      : []),
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  // ICS spec requires CRLF line endings
  return lines.join('\r\n');
}

// Triggers a "download" of the generated .ics file. On iOS Safari/WKWebView
// this is intercepted natively and opens the system "Add Event" sheet
// directly; on desktop/Android it downloads the file for the OS calendar
// app to pick up.
export function triggerIcsDownload(event: CalendarEventInput, filename = 'booking'): void {
  const content = buildIcsContent(event);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function getGoogleCalendarUrl(event: CalendarEventInput): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${toIcsUtc(event.startTime)}/${toIcsUtc(event.endTime)}`,
  });
  if (event.description) params.set('details', event.description);
  if (event.location) params.set('location', event.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export type MobilePlatform = 'ios' | 'android' | 'other';

export function detectMobilePlatform(): MobilePlatform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  // iPadOS 13+ reports as "Macintosh" but exposes touch support, unlike real Macs.
  if (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }
  return 'other';
}

// Adds the event straight to the platform's own calendar app with no
// picker: Android opens the Google Calendar deep-link, iOS/desktop
// downloads an .ics (which iOS's WebKit turns into the native "Add Event"
// sheet; desktop hands it to whatever app is registered for .ics).
export function addToCalendar(event: CalendarEventInput): void {
  if (detectMobilePlatform() === 'android') {
    window.open(getGoogleCalendarUrl(event), '_blank', 'noopener,noreferrer');
    return;
  }
  triggerIcsDownload(event);
}
